import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { SOLR_API } from "../envConstants";

// Define types for the filter parameters
export interface ContentFilters {
  contentCountryId?: number[];
  contentTypeId?: number[];
  contentGenreId?: number[];
  scheduledPublishedDateTo?: string; // ISO date string
  contentContinuumId?: number[];
  specialityId?: number[];
  topicId?: number[];
  sort?: string;
  status?: number; // Default is 489 as per the example
}

export interface PaginationOptions {
  page: number;
  pageSize: number;
}

export interface FetchContentOptions {
  filters: ContentFilters;
  pagination?: PaginationOptions;
  enabled?: boolean; // Whether to automatically fetch on mount/filters change
}

export interface ContentResponse {
  response: {
    docs: any[];
    numFound: number;
    start: number;
  };
  responseHeader: {
    status: number;
    QTime: number;
    params: any;
  };
}

const API_URL = `${SOLR_API}/select`;

const useFetchContent = (options: FetchContentOptions) => {
  const [data, setData] = useState<ContentResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const previousRequestRef = useRef<string>("");

  // Default pagination values
  const pagination = options.pagination || { page: 1, pageSize: 10 };

  // Function to build filter query array based on provided filters
  const buildFilterQuery = (filters: ContentFilters): string[] => {
    const fq: string[] = [];

    // Add default status filter
    fq.push(`status:${filters.status || 489}`);

    if (filters.contentCountryId?.length) {
      fq.push(`contentCountryId:(${filters.contentCountryId.join(" OR ")})`);
    }

    if (filters.contentTypeId?.length) {
      fq.push(`contentTypeId:(${filters.contentTypeId.join(" OR ")})`);
    }

    if (filters.contentGenreId?.length) {
      fq.push(`contentGenreId:(${filters.contentGenreId.join(" OR ")})`);
    }

    if (filters.contentContinuumId?.length) {
      fq.push(
        `contentContinuumId:(${filters.contentContinuumId.join(" OR ")})`
      );
    }

    if (filters.specialityId?.length) {
      fq.push(`speciality_id:(${filters.specialityId.join(" OR ")})`);
    }

    if (filters.topicId?.length) {
      fq.push(`topic_id:(${filters.topicId.join(" OR ")})`);
    }

    return fq;
  };

  // Function to create a request signature for comparing if request params have changed
  const createRequestSignature = (
    filters: ContentFilters,
    pagination: PaginationOptions
  ) => {
    return JSON.stringify({
      filters,
      pagination,
    });
  };

  // Function to perform the fetch
  const fetchContent = async (
    customFilters?: ContentFilters,
    customPagination?: PaginationOptions
  ) => {
    try {
      const filtersToUse = customFilters || options.filters;
      const paginationToUse = customPagination || pagination;

      // Create a signature for this request to avoid duplicate calls
      const requestSignature = createRequestSignature(
        filtersToUse,
        paginationToUse
      );

      // Skip if this exact request is already in progress or was just made
      if (requestSignature === previousRequestRef.current && loading) {
        return data;
      }

      // Update the request signature
      previousRequestRef.current = requestSignature;

      setLoading(true);
      setError(null);

      const filterQuery = buildFilterQuery(filtersToUse);

      // Calculate start index for pagination
      const start = (paginationToUse.page - 1) * paginationToUse.pageSize;

      const response = await axios.post<ContentResponse>(
        API_URL,
        {
          params: {
            q: "*:*",
            fq: filterQuery,
            sort: filtersToUse.sort || "publishedDate desc",
            start: start,
            rows: paginationToUse.pageSize,
          },
        },
        {
          headers: {
            authorization: "Basic c29sci1jbGllbnQ6VGF0dmFjYXJlQDE=",
            accept: "application/json",
          },
        }
      );

      setData(response.data);
      return response.data;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "An unknown error occurred";
      const newError = new Error(`Failed to fetch content: ${errorMessage}`);
      setError(newError);
      //   throw newError;
    } finally {
      setLoading(false);
    }
  };

  // Effect to trigger fetch when filters or pagination change
  useEffect(() => {
    // Skip if disabled
    if (options.enabled === false) {
      return;
    }

    // Create a signature for the current request parameters
    const requestSignature = createRequestSignature(
      options.filters,
      pagination
    );

    // Skip if this exact request was just made (prevents duplicate calls)
    if (requestSignature === previousRequestRef.current && data !== null) {
      return;
    }

    fetchContent();

    // Don't include data in the dependency array to prevent unnecessary refetches
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    options.enabled,
    // Use JSON.stringify to create a stable dependency reference
    JSON.stringify(options.filters),
    pagination.page,
    pagination.pageSize,
  ]);

  // Return the data, loading state, error, and a manual fetch function
  return {
    data,
    loading,
    error,
    refetch: fetchContent,
    // Helper function to go to next page
    nextPage: () =>
      fetchContent(options.filters, {
        ...pagination,
        page: pagination.page + 1,
      }),
    // Helper function to go to previous page
    prevPage: () => {
      if (pagination.page > 1) {
        return fetchContent(options.filters, {
          ...pagination,
          page: pagination.page - 1,
        });
      }
      return Promise.resolve(data);
    },
    // Current pagination state
    pagination,
  };
};

export default useFetchContent;
